import styled from 'styled-components';

// Components
import { EnvironmentOutlined } from '@ant-design/icons';
import { Title } from 'components/bos-modules';
import CustomScroll from 'components/CustomScroll';
import LandingPageHeader from 'components/headers/LandingPageHeader';
import Page from 'pages/Page';
import { Col, CustomSelect, Form, Row, Spin } from 'top-component-library';
import RadioInputToggle from 'top-component-library/Fields/RadioInputToggle';

// Constants
import breakpoints from 'constants/breakpoints';
import { footer, header } from 'constants/style';

export const MapAddressInputErrorMessage = styled.p`
  color: var(--color-error__regular);
  font-size: 0.75rem;
  font-style: italic;
  margin-bottom: 0;
  margin-top: 0.5rem;
`;
export const MapContainer = styled(Row)`
  background-color: transparent;
  display: flex;
  flex-basis: 100%;
  flex-grow: 0;
  flex-shrink: 1;
  justify-content: space-between;
  margin: 0 auto;
  min-height: 100%;
  overflow: hidden;
  position: relative;
  text-align: var(--content-align);
  z-index: 1;

  @media (min-width: ${breakpoints.md}px) {
    box-shadow: rgba(0, 0, 0, 0.15) 0 2px 3px 0;
    margin: initial;
    max-width: initial;
    padding-left: 1rem;
    padding-right: 0;
  }
`;

export const MapFulfillmentMethodForm = styled(Form)`
  .ant-form-item-control {
    margin: 0 auto;
    max-width: 30rem;

    @media (min-width: ${breakpoints.md}px) {
      margin: initial;
      max-width: none;
    }
  }
`;

export const MapFulfillmentMethodToggle = styled(RadioInputToggle)`
  && {
    border-bottom: 1px solid var(--color-neutral__line);

    padding-bottom: 1.5rem;

    @media (min-width: ${breakpoints.md}px) {
      border-bottom: 0;
      padding-bottom: 0.5rem;
    }

    .ant-radio-group {
      width: 100%;
    }

    label {
      justify-content: center;
      width: 50%;

      &.ant-radio-button-wrapper-checked {
        &::before {
          background-color: var(--color-button-landing-page__regular);
        }
      }
    }
  }
`;

export const MapCustomHeader = styled(LandingPageHeader)`
  z-index: 2;

  @media (min-width: ${breakpoints.md}px) {
    box-shadow: 0 4px 4px rgba(136, 136, 136, 0.25);
  }
`;

export const MapInputContainer = styled.div`
  padding-top: 1.5rem;
`;

export const MapLabel = styled.label`
  color: var(--color-primary__regular);
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: 1.2;
  margin-bottom: 0.75rem;

  @media (min-width: ${breakpoints.md}px) {
    color: var(--color-neutral__title-active);
    font-size: 0.875rem;
    opacity: 0.75;
  }
`;

export const MapLoadingSpinner = styled(Spin)`
  color: var(--color-primary__regular);
`;

export const MapLoadingSpinnerContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;

  p {
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.4;
    margin: 2rem 0 0;
    opacity: 0.75;
    padding: 0 15%;
    text-align: center;

    @media (min-width: ${breakpoints.md}px) {
      padding: 0 20%;
    }
  }
`;

export const MapLocationsCount = styled.p`
  color: var(--color-neutral__title-active);
  font-size: 0.875rem;
  font-weight: bold;
  letter-spacing: 0.5px;
  line-height: 1;
  padding-bottom: 1.25rem;
`;

export const MapLocationsList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 2rem 1rem;

  > div:not(:last-child) {
    margin-bottom: 1rem;
  }

  @media (min-width: ${breakpoints.md}px) {
    min-height: calc(100% - 11rem);
    padding: 2.25rem 0;

    > div:not(:last-child) {
      margin-bottom: 2rem;
    }
  }
`;

export const MapLocationPinIcon = styled(EnvironmentOutlined)`
  align-items: center;
  display: flex;
  height: 6rem;
  justify-content: center;
  position: relative;
  width: 6rem;

  // Outer circle
  &::before {
    background-color: var(--color-neutral__placeholder);
    border-radius: 50%;
    display: block;
    height: 7rem;
    opacity: 0.0625;
    position: absolute;
    width: 7rem;

    @media (min-width: ${breakpoints.md}px) {
      content: '';
    }
  }

  // Inner circle
  &::after {
    background-color: var(--color-neutral__placeholder);
    border-radius: 50%;
    content: '';
    height: 6rem;
    opacity: 0.125;
    position: absolute;
    width: 6rem;
  }

  // Icon
  svg {
    height: 3.5rem;
    opacity: 0.5;
    width: 3.5rem;
  }
`;

export const MapMapColumn = styled(Col)`
  display: flex;
  flex-basis: auto;
  flex-direction: column;
  justify-content: center;
  width: 50%;

  @media (min-width: ${breakpoints.lg}px) {
    width: 52.5%;
  }

  @media (min-width: ${breakpoints.xl}px) {
    width: 55%;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    width: 57.5%;
  }

  @media (min-width: ${breakpoints.ultraWide}px) {
    width: 60%;
  }
`;

export const MapPage = styled(Page)`
  position: relative;
`;

export const MapScrollableContainer = styled(CustomScroll)`
  @media (min-width: ${breakpoints.md}px) {
    padding-right: 0;
  }

  &::-webkit-scrollbar {
    width: 17px;
  }

  &::-webkit-scrollbar-track {
    border-bottom: 0;
    border-right: 0;
    border-top: 0;
  }

  &::-webkit-scrollbar-thumb {
    border-bottom: 0;
    border-right: 0;
    border-top: 0;
  }
`;

export const MapSearchColumn = styled(Col)`
  display: flex;
  flex-basis: auto;
  flex-direction: column;
  min-height: 100%;
  width: 100%;

  @media (min-width: ${breakpoints.md}px) {
    display: block;
    margin: 0 auto;
    max-height: calc(100vh - ${header.desktop.height}px - ${footer.desktop.height}px);
    max-width: 30rem;
    min-height: initial;
    width: 90%;
  }
`;

export const MapSearchColumnTop = styled(Col)`
  box-shadow: rgba(0, 0, 0, 0.15) 0 2px 3px 0;
  padding: 2rem 1rem;

  @media (min-width: ${breakpoints.md}px) {
    border-bottom: 1px solid var(--color-neutral__line);
    box-shadow: none;
    padding: 6rem 0 2.25rem;
  }
`;

export const MapTitle = styled(Title)`
  margin-bottom: ${({ reduceMargin }) => (reduceMargin ? '1rem' : '2rem')};
  padding: 0 15%;

  @media (min-width: ${breakpoints.md}px) {
    margin-bottom: ${({ reduceMargin }) => (reduceMargin ? '2rem' : '3.5rem')};
    padding: 0;
  }
`;

export const MapVendorSelect = styled(CustomSelect)``;

export default Map;
